import * as gqlTypes from "@hydrologiq/gql/graphql"
import { useState } from "react"
import { useMutation } from "@apollo/client"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { Input, VStack } from "@chakra-ui/react"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"

export interface FillingSiteCreationFormProps {
  onSuccess: (fillingSite: gqlTypes.FillingSite) => void
  onCancel?: () => void
  onBack?: () => void
  enableLocation?: boolean
  mapsApiKey?: string
}

/**
 * Shared filling site creation form component.
 * Can be used across different sections without tight coupling to routing or section-specific state.
 * Note: Location feature is optional and controlled by enableLocation prop.
 */
const FillingSiteCreationForm = ({
  onSuccess,
  onCancel,
  onBack,
  enableLocation = false,
  mapsApiKey,
}: FillingSiteCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)
  const [createFillingSite, { loading }] = useMutation(gqlTypes.CreateFillingSiteDocument, {
    // Refetch all active GetFillingSites queries (regardless of variables)
    // This ensures the new fill site appears in the table with correct pagination/filtering
    refetchQueries: ["GetFillingSites"],
    awaitRefetchQueries: false, // Don't wait - let it happen in background
  })

  const handleSubmit = async (data: FormData) => {
    try {
      setError(null)
      const name = data.get("name") as string
      const locationId = data.get("locationId") as string

      // Validation
      if (!name || !name.trim()) {
        const errorMessage = "Please provide a name for the fill site"
        setError(errorMessage)
        showErrorToast("Validation Error", errorMessage)
        return false
      }

      let vars: any = {
        name: name.trim(),
      }

      // Add location ID if feature is enabled and location is selected
      if (enableLocation && locationId && locationId !== "new" && locationId !== "") {
        vars.location = locationId
      }

      const { data: fillingSiteData } = await createFillingSite({
        variables: vars,
      })

      if (fillingSiteData?.createFillingSite) {
        showSuccessToast(
          "Fill site created",
          `Fill site "${fillingSiteData.createFillingSite.site.name}" has been successfully created.`
        )
        setError(null)

        // Pass created filling site to parent
        onSuccess(fillingSiteData.createFillingSite)

        return true
      } else {
        const errorMessage = "Failed to create fill site. Please try again."
        setError(errorMessage)
        showErrorToast("Creation Failed", errorMessage)
        return false
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred while creating the fill site"
      setError(errorMessage)
      showErrorToast("Creation Failed", errorMessage)
      return false
    }
  }

  const handleCancel = () => {
    setError(null)
    if (onCancel) onCancel()
  }

  const handleBack = () => {
    setError(null)
    if (onBack) onBack()
  }

  return (
    <Form
      heading="Create a fill site"
      helperText="Please provide fill site information below."
      actionType="create"
      loading={loading}
      onSubmit={handleSubmit}
      onCancel={onCancel ? handleCancel : undefined}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5} width="100%">
        {error && <ErrorAlert title="Fill site creation error" message={error} onClose={() => setError(null)} />}
        <Field label="Name" required>
          <Input name="name" type="text" placeholder="Enter fill site name" />
        </Field>
        {/* Location feature can be added here if enableLocation is true */}
        {/* For now, keeping it simple without location selector to avoid additional dependencies */}
      </VStack>
    </Form>
  )
}

export default FillingSiteCreationForm
